import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCompanies } from '../../api/companies.js';
import CompanyFormModal from '../CompanyFormModal.jsx';
import Spinner from '../Spinner.jsx';
import ErrorBanner from '../ErrorBanner.jsx';

// readOnly hides creating/editing companies. allOption, when set, adds a first entry with that
// label that calls onSelect(undefined) and is active while no company is selected.
export default function CompaniesPanel({ selectedCompanyId, onSelect, readOnly = false, allOption }) {
    const { t } = useTranslation();
    const { data: companies, isLoading, error } = useCompanies();
    const [showCreate, setShowCreate] = useState(false);
    const [editingCompany, setEditingCompany] = useState(null);

    return (
        <div className="workspace-panel">
            <div className="panel-header">
                <h2>{t('companies.title')}</h2>
                {!readOnly && (
                    <button type="button" className="btn btn-sm btn-primary" onClick={() => setShowCreate(true)}>
                        {t('companies.new')}
                    </button>
                )}
            </div>

            <div className="panel-body">
                {isLoading && <Spinner />}
                <ErrorBanner error={error} fallback={t('companies.loadError')} />

                {!isLoading && companies?.length === 0 && <div className="panel-empty">{t('companies.empty')}</div>}

                {allOption && (
                    <div
                        role="button"
                        tabIndex={0}
                        className={`panel-list-item ${selectedCompanyId === undefined ? 'active' : ''}`}
                        onClick={() => onSelect(undefined)}
                        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSelect(undefined)}
                    >
                        <span className="item-title">{allOption}</span>
                    </div>
                )}

                {companies?.map((company) => (
                    <div
                        key={company.id}
                        role="button"
                        tabIndex={0}
                        className={`panel-list-item ${company.id === selectedCompanyId ? 'active' : ''}`}
                        onClick={() => onSelect(company.id)}
                        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSelect(company.id)}
                    >
                        <span className="item-title">{company.name}</span>
                        <span className="item-meta">{t('companies.rucLabel', { ruc: company.id })}</span>
                        {!readOnly && (
                            <button
                                type="button"
                                className="btn btn-sm item-meta"
                                style={{ marginTop: '0.35rem' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingCompany(company);
                                }}
                            >
                                {t('companies.edit')}
                            </button>
                        )}
                    </div>
                ))}
            </div>

            {!readOnly && showCreate && (
                <CompanyFormModal onClose={() => setShowCreate(false)} onSaved={(data) => onSelect(data.id)} />
            )}
            {!readOnly && editingCompany && (
                <CompanyFormModal
                    company={editingCompany}
                    onClose={() => setEditingCompany(null)}
                    onSaved={(data) => onSelect(data.id)}
                />
            )}
        </div>
    );
}